import { useEffect, useState } from 'react';
import { Drawer, Skeleton, Empty, Button, Space, Typography, Tooltip } from 'antd';
import {
  FileTextOutlined,
  ExportOutlined,
  ReloadOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import {
  getValidationReportByRunId,
  type ValidationReportMeta,
} from '@/api/validationRuns';
import { colors } from '@/theme/tokens';
import { useStore } from '@/stores';

const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────────────────
// HtmlReportDrawer — full-width viewer for a validation run's HTML report
// ─────────────────────────────────────────────────────────────────────────────
//
// PURPOSE
//   The validation framework writes a formatted, chat-bubble HTML report
//   for every run (uploaded to blob storage). This drawer lets a clinical
//   SME read that report inline at 90% width — wide enough for multi-turn
//   conversations to render without horizontal scroll, narrow enough that
//   the underlying page (where they came from) stays visible on the left
//   for context.
//
// CONTRACT
//   * Open it by setting `runId` to a number; close by setting it to null
//     (the parent owns the open state).
//   * Re-fetches the SAS URL every time the drawer opens — SAS tokens
//     expire, and the freshest URL is what the iframe needs.
//   * Sandboxed iframe: `allow-same-origin` so the report's relative
//     CSS/fonts work, and nothing else. No `allow-scripts` — the report
//     is presentation-only by design.
//   * Friendly empty state when no report exists (e.g. older runs that
//     pre-dated the cloud-upload cutover).
//
// NURSE-FRIENDLY UX (reinforced by feedback memory)
//   * Plain language. No "blob", "SAS", or "sharedAccessKey" anywhere
//     the user can see.
//   * Header shows run-context badges (pass/fail, scenario count, run
//     timestamp) so the user knows WHICH run they're looking at.
//   * "Open in new tab" lives in the header for full-screen viewing.
//   * Reload button surfaces if the SAS URL refresh is needed mid-session.
// ─────────────────────────────────────────────────────────────────────────────

interface Props {
  runId: number | null;
  onClose: () => void;
  /** Optional — surface pass/fail context above the iframe. */
  passed?: boolean | null;
  /** Optional — shown next to title (e.g. "FOLFOX Patient · 2 min ago"). */
  subtitle?: string;
}

export function HtmlReportDrawer({ runId, onClose, passed, subtitle }: Props) {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';

  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState<ValidationReportMeta | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [iframeKey, setIframeKey] = useState(0);

  // Fetch fresh on open — SAS URLs are time-limited and a stale URL would
  // give the user an opaque "AuthorizationFailed" page. Better to re-pull.
  useEffect(() => {
    if (runId == null) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setReport(null);
    void (async () => {
      try {
        const meta = await getValidationReportByRunId(runId);
        if (!cancelled) {
          setReport(meta);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          // eslint-disable-next-line no-console
          console.error('[HtmlReportDrawer] failed to load report:', err);
          setError(
            'We could not load this report. Try the reload button, or contact support if it keeps happening.',
          );
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [runId]);

  const reload = () => {
    setIframeKey((k) => k + 1);
    if (runId != null) {
      // Force a fresh metadata fetch by toggling state
      setLoading(true);
      void getValidationReportByRunId(runId).then(
        (m) => {
          setReport(m);
          setLoading(false);
        },
        () => setLoading(false),
      );
    }
  };

  return (
    <Drawer
      open={runId != null}
      onClose={onClose}
      width="90%"
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
          <FileTextOutlined style={{ color: colors.violet[600], fontSize: 18 }} />
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 600, lineHeight: 1.2 }}>
              Run report {runId != null ? `#${runId}` : ''}
            </div>
            {subtitle && (
              <Text
                type="secondary"
                style={{ fontSize: 12, lineHeight: 1.4, display: 'block' }}
              >
                {subtitle}
              </Text>
            )}
          </div>
          {/* Pass/fail context badge — non-intrusive, reinforces what the user is looking at. */}
          {passed != null && (
            <span
              style={{
                fontSize: 12,
                fontWeight: 500,
                padding: '4px 10px',
                borderRadius: 999,
                background: passed
                  ? 'rgba(5, 150, 105, 0.12)'
                  : 'rgba(220, 38, 38, 0.12)',
                color: passed ? '#059669' : '#DC2626',
                whiteSpace: 'nowrap',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              {passed ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
              {passed ? 'Passed' : 'Failed'}
            </span>
          )}
        </div>
      }
      extra={
        <Space size="small">
          <Tooltip title="Refresh the report">
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={reload}
              disabled={loading || runId == null}
            />
          </Tooltip>
          <Tooltip title="Open the report in a new browser tab">
            <Button
              size="small"
              icon={<ExportOutlined />}
              disabled={!report?.blobWithSharedAccessKey}
              onClick={() => {
                if (report?.blobWithSharedAccessKey) {
                  window.open(report.blobWithSharedAccessKey, '_blank', 'noopener,noreferrer');
                }
              }}
            >
              Open in new tab
            </Button>
          </Tooltip>
        </Space>
      }
      styles={{
        body: { padding: 0, background: isDark ? colors.slate[900] : '#FFFFFF' },
      }}
      destroyOnClose
    >
      {loading ? (
        <div style={{ padding: 24 }}>
          <Skeleton active paragraph={{ rows: 8 }} />
          <div
            style={{
              fontSize: 12,
              color: isDark ? colors.slate[400] : colors.slate[500],
              marginTop: 12,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <ClockCircleOutlined /> Loading the report…
          </div>
        </div>
      ) : error ? (
        <div style={{ padding: 24 }}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span style={{ color: isDark ? colors.slate[300] : colors.slate[600], fontSize: 13 }}>
                {error}
              </span>
            }
          >
            <Button onClick={reload}>Try again</Button>
          </Empty>
        </div>
      ) : !report?.blobWithSharedAccessKey ? (
        <div style={{ padding: 24 }}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <div style={{ color: isDark ? colors.slate[300] : colors.slate[600], fontSize: 13 }}>
                <div style={{ marginBottom: 6 }}>No report is available for this run.</div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Older runs that finished before reports moved to the cloud may not have one.
                </Text>
              </div>
            }
          />
        </div>
      ) : (
        // ── The viewer itself ───────────────────────────────────────────
        // sandbox="allow-same-origin" lets the report's CSS / fonts /
        // images load. We deliberately omit `allow-scripts` — the
        // report is static markup; we don't want any JS in it executing
        // in our origin even by accident.
        <iframe
          key={iframeKey}
          src={report.blobWithSharedAccessKey}
          title={`Run report ${runId}`}
          style={{
            width: '100%',
            height: 'calc(100vh - 120px)',
            border: 'none',
            background: '#FFFFFF',
          }}
          sandbox="allow-same-origin allow-popups"
        />
      )}
    </Drawer>
  );
}
